import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { visitService } from '../../services/visit.service';
import { employeeService } from '../../services/employee.service';
import { DataTable } from '../../components/common/DataTable';
import { StatusChip } from '../../components/common/StatusChip';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { PageHeader } from '../../components/common/PageHeader';
import type { VisitAssignment } from '../../types';
import { VisitStatus } from '../../types';

const today = format(new Date(), 'yyyy-MM-dd');

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: VisitStatus.PENDING, label: 'Pending' },
  { value: VisitStatus.COMPLETED, label: 'Completed' },
  { value: VisitStatus.MISSED, label: 'Missed' },
  { value: VisitStatus.CANCELLED, label: 'Cancelled' },
];

export function VisitManagementPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterDate, setFilterDate] = useState(today);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [statusDialog, setStatusDialog] = useState<{
    visit: VisitAssignment;
    newStatus: VisitStatus;
  } | null>(null);
  const [statusNotes, setStatusNotes] = useState('');

  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['team-visits', page, rowsPerPage, filterDate, filterEmployee, filterStatus],
    queryFn: () =>
      visitService.getTeamVisits({
        page: page + 1,
        limit: rowsPerPage,
        date: filterDate || undefined,
        employeeId: filterEmployee || undefined,
        status: (filterStatus as VisitStatus) || undefined,
      }),
  });

  const { data: employees } = useQuery({
    queryKey: ['my-team'],
    queryFn: () => employeeService.getMyTeam(),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: VisitStatus; notes?: string }) =>
      visitService.updateStatus(id, status, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-visits'] });
      toast.success('Visit status updated');
      setStatusDialog(null);
      setStatusNotes('');
    },
    onError: () => toast.error('Failed to update status'),
  });

  const columns = [
    {
      id: 'employee',
      label: 'Employee',
      minWidth: 150,
      render: (row: VisitAssignment) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{row.employee?.name ?? '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{row.employee?.employeeCode}</Typography>
        </Box>
      ),
    },
    {
      id: 'customer',
      label: 'Customer',
      minWidth: 150,
      render: (row: VisitAssignment) => (
        <Typography variant="body2">{row.customer?.name ?? '—'}</Typography>
      ),
    },
    {
      id: 'date',
      label: 'Date & Time',
      render: (row: VisitAssignment) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {format(new Date(row.assignedDate), 'MMM d, yyyy')}
          </Typography>
          {row.scheduledTime && (
            <Typography variant="caption" color="text.secondary">{row.scheduledTime}</Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'priority',
      label: 'Priority',
      render: (row: VisitAssignment) => <StatusChip status={row.priority} />,
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: VisitAssignment) => <StatusChip status={row.status} />,
    },
    {
      id: 'actions',
      label: 'Update Status',
      align: 'right' as const,
      render: (row: VisitAssignment) => (
        row.status === VisitStatus.PENDING ? (
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setStatusDialog({ visit: row, newStatus: VisitStatus.CANCELLED });
              }}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={(e) => {
                e.stopPropagation();
                setStatusDialog({ visit: row, newStatus: VisitStatus.MISSED });
              }}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Mark Missed
            </Button>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {row.completedAt
              ? `Done at ${format(new Date(row.completedAt), 'HH:mm')}`
              : '—'}
          </Typography>
        )
      ),
    },
  ];

  if (error) {
    const apiMessage =
      (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
      ?? (error as Error)?.message;
    return (
      <ErrorMessage
        title="Could not load visits"
        message={apiMessage || 'Failed to load team visits. Please try again.'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        compact
        title="Visit Management"
        subtitle={`${data?.total ?? 0} visits for your team`}
      />

      {/* Filters */}
      <Box display="flex" gap={1.5} mb={2.5} flexWrap="wrap">
        <TextField
          size="small"
          label="Date"
          type="date"
          value={filterDate}
          onChange={(e) => { setFilterDate(e.target.value); setPage(0); }}
          sx={{ minWidth: 160 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Employee"
          select
          value={filterEmployee}
          onChange={(e) => { setFilterEmployee(e.target.value); setPage(0); }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Employees</MenuItem>
          {employees?.map((e) => (
            <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Status"
          select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
          sx={{ minWidth: 150 }}
        >
          {STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => { setFilterDate(today); setFilterEmployee(''); setFilterStatus(''); setPage(0); }}
          sx={{ height: 40 }}
        >
          Reset
        </Button>
      </Box>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No visits found for your team."
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.total}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
      />

      {/* Status Update Dialog */}
      <Dialog open={!!statusDialog} onClose={() => setStatusDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {statusDialog?.newStatus === VisitStatus.MISSED ? 'Mark as Missed' : 'Cancel Visit'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {statusDialog?.newStatus === VisitStatus.MISSED
              ? 'Mark this visit as missed for tracking purposes.'
              : 'Cancel this visit assignment.'}
          </Typography>
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={2}
            value={statusNotes}
            onChange={(e) => setStatusNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setStatusDialog(null)} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={statusDialog?.newStatus === VisitStatus.MISSED ? 'warning' : 'error'}
            disabled={statusMutation.isPending}
            onClick={() =>
              statusDialog &&
              statusMutation.mutate({
                id: statusDialog.visit.id,
                status: statusDialog.newStatus,
                notes: statusNotes,
              })
            }
            startIcon={statusMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {statusMutation.isPending ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
